import React, { useCallback, useEffect, useState } from "react";
import PlannerHeader from "./PlannerHeader";
import { useDispatch, useSelector } from "react-redux";
import {
  selectWeeklyPlan,
  selectWeeklyPlanIsLoading,
  updateWeeklyPlanDates,
} from "@/store/slices";
import { WeeklyPlanStateData } from "@/models";
import { useRosterInfo } from "@/hooks";
import { calculateEndDate } from "@/utilities";
import { CurriculumIcons } from "@/constants/CurriculumIcons";
import { useNavigate } from "react-router";
import { ModalPortal } from "@/components/Global";
import { LearningTimeModal } from "./LearningTimeModal";
import { useWeeklyPlan } from "@/hooks";
import { MasteryTimeBooked } from "@/enums";

enum PlannerModalState {
  NONE,
  LEARNING_TIME,
  MASTERY,
  PLAYLIST,
}

interface PlannerHeaderContainerProps {
  isFullHeader: boolean;
  handleSetWeekLoading: (loading: boolean) => void;
}

const PlannerHeaderContainer: React.FC<PlannerHeaderContainerProps> = ({
  isFullHeader,
  handleSetWeekLoading
}) => {
  // - START FAKE DATA FOR DEV PURPOSES
  const weekNum = 11; // TODO need to retrieve from curriculum service maybe?
  const { data, skillsMasteryLoaded } = useSelector(selectWeeklyPlan);
  const loading = useSelector(selectWeeklyPlanIsLoading);
  const { dateFrom, dateTo, curriculum, curTrack } =
    data as WeeklyPlanStateData;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(new Date(`${dateFrom}T00:00:00`));
  const [endDate, setEndDate] = useState(new Date(`${dateTo}T00:00:00`));
  const [week, setWeek] = useState(weekNum);
  const [weekLoading, setWeekLoading] = useState(false);
  const { students } = useRosterInfo();
  const [modalState, setModalState] = useState<PlannerModalState>(() => {
    const savedState = sessionStorage.getItem("PlannerModalState");
    return savedState ? JSON.parse(savedState) : PlannerModalState.NONE;
  });
  const { getLessonBookingStatus } = useWeeklyPlan();

  const nextWeek = useCallback(() => {
    const nextWeekStart = calculateEndDate(7, startDate);
    setStartDate(nextWeekStart);
    setEndDate(calculateEndDate(4, nextWeekStart));
    setWeek(week + 1); //WEEK DATA should be coming from the backend
    setWeekLoading(true);
    handleSetWeekLoading(true);
  }, [startDate, calculateEndDate, week]);

  const prevWeek = useCallback(() => {
    const prevWeekStart = calculateEndDate(-7, startDate);
    setStartDate(prevWeekStart);
    setEndDate(calculateEndDate(4, prevWeekStart));
    setWeek(week - 1); //WEEK DATA should be coming from the backend
    setWeekLoading(true);
    handleSetWeekLoading(true);
  }, [startDate, calculateEndDate, week]);

  const handleStudentPlaylistPress = () => {
    navigate(`/planner/student-learning-playlist`);
  };

  const handlePlannerPress = () => {
    navigate(`/planner`);
  };

  const onLearningTimeClick = () =>
    setModalState(PlannerModalState.LEARNING_TIME);
  const onLearningTimeClose = () => setModalState(PlannerModalState.NONE);

  // debounce the week change dispatch
  useEffect(() => {
    const timer = setTimeout(() => {
      const lessonStartDate = new Date(`${dateFrom}T00:00:00`);
      if (lessonStartDate.getTime() !== startDate.getTime()) {
        dispatch(
          updateWeeklyPlanDates({
            dateFrom: startDate.toISOString().split("T")[0],
            dateTo: calculateEndDate(4, startDate).toISOString().split("T")[0],
          })
        );
      }
      setWeekLoading(false);
      handleSetWeekLoading(false);
    }, 1000); // 1-second debounce delay

    // Cleanup the timeout if the component unmounts or inputValue changes
    return () => clearTimeout(timer);
  }, [startDate]);

  // Side effects
  useEffect(() => {
    sessionStorage.setItem("PlannerModalState", JSON.stringify(modalState));
  }, [modalState]);

  const getTotalAlerts = useCallback(() => {
    if (!data?.curriculum?.tracks) return { overbooked: 0, underbooked: 0 };

    let totalOverbooked = 0;
    let totalUnderbooked = 0;

    data.curriculum.tracks.forEach((track) => {
      track.units?.forEach((unit) => {
        unit.lessons?.forEach((lesson) => {
          const bookingStatus = getLessonBookingStatus(lesson.lessonPlanId);
          if (bookingStatus[MasteryTimeBooked.OVER_BOOKED]) {
            totalOverbooked +=
              bookingStatus[MasteryTimeBooked.OVER_BOOKED].size;
          }
          if (bookingStatus[MasteryTimeBooked.UNDER_BOOKED]) {
            totalUnderbooked +=
              bookingStatus[MasteryTimeBooked.UNDER_BOOKED].size;
          }
        });
      });
    });

    return { overbooked: totalOverbooked, underbooked: totalUnderbooked };
  }, [data, getLessonBookingStatus]);

  return (
    <>
      <PlannerHeader
        curriculumIcon={
          curriculum.name
            ? (CurriculumIcons[
                curriculum.name.toLowerCase().replaceAll(" ", "_")
              ] ?? CurriculumIcons["default"])
            : ""
        }
        curriculumName={curriculum.name}
        loading={loading || weekLoading}
        disableWeekSwitching={loading}
        weekNum={week}
        dateFrom={startDate}
        dateTo={endDate}
        prevWeekHandler={prevWeek}
        nextWeekHandler={nextWeek}
        handleStudentPlaylistPress={handleStudentPlaylistPress}
        handlePlannerPress={handlePlannerPress}
        onLearningTimeClick={onLearningTimeClick}
        isFullHeader={isFullHeader}
        totalBookingAlerts={getTotalAlerts()}
      />
      <ModalPortal isOpen={modalState === PlannerModalState.LEARNING_TIME}>
        <ModalPortal.Header
          title={"Manage Assignments to Build Mastery"}
          onClose={onLearningTimeClose}
        />
        <LearningTimeModal />
      </ModalPortal>
    </>
  );
};

export default PlannerHeaderContainer;
